import { useState, useEffect } from 'react';
import { Flex, Form, message, Spin } from 'antd';
import TicketFormBuilder from './TicketFormBuilder';
import { useCreateTicket, useFetchTicketFormFields } from '@/hooksApi';
import { useTranslation } from 'react-i18next';
import { Heading } from '@/components/Heading';
import { SingleTicketResponse, TicketField } from '@/types';
import { useTicketingStore } from '@/stores';
import { useQueryClient } from '@tanstack/react-query';
import {
  issueFieldList,
  otherIssueFieldByForm,
  TicketDescriptionField,
  TicketFormsEnum,
} from '@/constants';
import useLanguageStore from '@/stores/useLanguageStore';
import { getFormData } from '@/utils';
import usePermission from '@/hooks/usePermission';
import { PermissionName } from '@/types';

export default function TicketFormContainer() {
  const [isCreatingTicket, setIsSubmittingTicket] = useState<boolean>(false);
  const { language } = useLanguageStore();

  const { mutate: createTicket } = useCreateTicket();

  const { hasPermission } = usePermission();
  const { data: formFieldList, isLoading } = useFetchTicketFormFields(
    language,
    hasPermission(PermissionName.CREATE_TICKET),
  );

  const { ticketFormData, closeModal } = useTicketingStore();
  const queryClient = useQueryClient();

  const [form] = Form.useForm();
  const { t } = useTranslation();

  const currentForm = getFormData(
    ticketFormData?.ticket_form_id ?? TicketFormsEnum.APP,
    formFieldList?.object ?? [],
  );

  const isIssueField = (field?: TicketField) => {
    if (!field) return false;
    return issueFieldList.includes(field.id);
  };

  const handleSubmit = (_values: any) => {
    setIsSubmittingTicket(true);

    // 🔴 FOUND FROM THE FORM DEFINITION, NOT FROM THE SUBMITTED VALUES, AND
    // THAT DISTINCTION IS THE WHOLE BUG.
    //
    // This used to be assigned inside the loop below, which walks the values
    // Ant Design collected. `TicketFormBuilder` returns null for every field in
    // `issueFieldList` (it is not rendered — R10 removed it), so the issue
    // field is never in `values`, so this stayed undefined, so the catch-all
    // block further down never ran, so no issue value was sent, so:
    //
    //     POST /v1/merchants/tickets -> 400 "Missing required fields: Order Issues"
    //
    // Every dashboard ticket creation failed. The catch-all was written and is
    // correct; it was simply unreachable. Removing a CONTROL silently removed
    // the INPUT the fallback was reading, which is a quieter way to break
    // something than deleting the fallback would have been.
    //
    // ⭐ The mobile app never had this bug because it walks the FORM'S OWN
    // fields (`_selectedForm!.fields`) rather than the user's answers — the
    // reason the same submission succeeds there and fails here.
    const currentIssueField = currentForm?.fields.find((field) => isIssueField(field)) as
      | TicketField
      | undefined;

    const { attachments, description, ...values } = _values;
    const custom_fields: { id: number; value: string | null }[] = [];
    const normal_fields: { id: number; value: string | null; title: string | null }[] = [];

    Object.entries(values).forEach(([id, value]) => {
      const field = currentForm?.fields.find((f) => f.id === Number(id));

      if (field?.custom) {
        custom_fields.push({
          id: Number(id),
          value: value ? String(value) : null,
        });
      } else {
        normal_fields.push({
          id: Number(id),
          value: value ? String(value) : null,
          title: field?.type ? String(field?.type) : null,
        });
      }
    });

    const selectedIssueValue: string | undefined = currentIssueField
      ? values[currentIssueField?.id]
      : undefined;

    const selectedIssue = currentIssueField?.custom_field_options.find(
      (opt) => opt.value === selectedIssueValue,
    );
    // R10/R11 — the merchant no longer picks a category, so `selectedIssue` is
    // now ALWAYS undefined and this branch always takes the "others" path.
    //
    // 🔴 IT IS LOAD-BEARING, AND FOR A DIFFERENT REASON THAN I FIRST WROTE.
    // The original note here claimed it preserved R11's reporting by putting a
    // valid category on the ticket. It does not — `attributes` is `{}` on 0/92
    // staging tickets, so the issue value is accepted at the boundary and
    // stored nowhere at all.
    //
    // What it actually does is keep ticket creation WORKING. The API still
    // rejects a ticket with no issue value:
    //     POST /v1/merchants/tickets -> 400 "Missing required fields: Order Issues"
    // Delete this branch and every merchant ticket 400s. (The app had no
    // equivalent branch, removed the dropdown, and would have done exactly
    // that — caught on staging before it shipped.)
    //
    // ✅ R11's PROMISE SURVIVES ANYWAY, by a route neither of us was looking
    // at: reporting groups by `category`, which is derived from
    // `ticket_form_id` and stored on every ticket. R10 does not change which
    // form the merchant uses, so that grouping is untouched. Right conclusion,
    // wrong mechanism — recorded because the next person would rely on the
    // mechanism.
    //
    // ⚠️ `otherIssueFieldByForm` is per-FORM on purpose: `payment_issue` uses
    // `other` (singular) while the other three use `others`. Verified against
    // production by BOXY::BACKEND. A single shared constant here would
    // mismatch on payment tickets and nothing would error.
    if (currentIssueField && currentForm?.id) {
      const OTHERS_ID = otherIssueFieldByForm[currentForm.id as TicketFormsEnum];
      if (!selectedIssue) {
        // 🔴 A NULL HERE IS THE 400 COMING BACK. The append loop below skips
        // null values, so a missed lookup does not error — it silently sends
        // no issue value and the API answers "Missing required fields: Order
        // Issues" again. The ID match is first preference (verified against
        // all four production forms), but option IDs are environment data:
        // staging's Zendesk can differ from the hardcoded prod enums. So the
        // fallbacks are the mobile app's exact ladder (_catchAllOptionValue):
        // match the catch-all by VALUE — prod spells it `others` on three
        // forms and `other`, singular, on payment — and failing that take the
        // LAST option, because a slightly wrong catch-all on an unrecognised
        // form still beats no ticket at all.
        const options = currentIssueField.custom_field_options;
        const catchAll =
          options.find((option) => option.id === OTHERS_ID) ??
          options.find((option) =>
            ['others', 'other'].includes(String(option.value).toLowerCase()),
          ) ??
          options[options.length - 1];
        custom_fields.push({
          id: currentIssueField.id,
          value: catchAll?.value ?? null,
        });
      }
    }

    // A description is now the ONLY thing the merchant must supply. The old
    // gate accepted "an issue selection OR a description", which was reasonable
    // while the dropdown existed — picking a category was itself a statement of
    // the problem. With the dropdown gone that clause can never be satisfied,
    // and leaving it in would imply something else could still stand in for the
    // message. Nothing can.
    if (!description) {
      message?.error(t('please_enter_description'));
      setIsSubmittingTicket(false);
      return;
    }

    const formData = new FormData();
    if (currentForm?.id) formData.append('type', currentForm.id.toString());
    formData.append('ticket_form_id', currentForm?.id?.toString() ?? '');

    (attachments as any[])?.forEach((file) => {
      if (file.originFileObj) {
        formData.append(`attachments[]`, file.originFileObj);
      }
    });

    custom_fields?.forEach((field, index) => {
      if (!!field.value && !!field.id) {
        //skip null values
        formData.append(`custom_fields[${index}][id]`, field.id.toString());
        formData.append(`custom_fields[${index}][value]`, field.value.toString());
      }
    });

    normal_fields?.forEach((field, index) => {
      if (field.value) {
        formData.append(`fields[${index}][title]`, field.title!.toString());
        formData.append(`fields[${index}][value]`, field.value ?? '');
      }
    });

    // Append description field
    if (description || selectedIssueValue) {
      const descIndex = normal_fields.length;
      formData.append(`fields[${descIndex}][title]`, 'description');
      formData.append(`fields[${descIndex}][value]`, description ?? selectedIssue?.name ?? '');
    }

    createTicket(formData, {
      onSuccess: () => {
        message?.success(t('operation_completed_successfully'));
        queryClient.invalidateQueries(['tickets']);
        setIsSubmittingTicket(false);
        handleCloseModal();
      },
      onError: () => {
        setIsSubmittingTicket(false);
      },
    });
  };

  const transferToFormValue = (ticketData: SingleTicketResponse) => {
    if (!ticketData) return {};

    const formValues: Record<string, any> = {};

    formValues['subject'] = ticketData.subject;
    formValues['description'] = ticketData.description;

    // custom_fields
    ticketData.custom_fields?.forEach((field) => {
      formValues[String(field.id)] = field.value;
    });

    // Set uid field value from entity_uid
    if (ticketData.entity_uid && currentForm?.fields) {
      const uidField = currentForm.fields.find((field) => field.key === 'uid');
      if (uidField) {
        formValues[String(uidField.id)] = ticketData.entity_uid;
      }
    }

    return formValues;
  };

  const handleCloseModal = () => {
    form.resetFields();
    closeModal();
  };

  useEffect(() => {
    if (formFieldList) {
      form.setFieldsValue(ticketFormData ? transferToFormValue(ticketFormData) : undefined);
    }
  }, [ticketFormData, formFieldList]);

  const isFromEntityDetailPage =
    !!ticketFormData && ticketFormData.custom_fields?.length === 1 && !ticketFormData.id; // if is creating from order/remittance/.. pages

  return (
    <div className='mt-2'>
      <Form
        initialValues={ticketFormData ? transferToFormValue(ticketFormData) : undefined}
        layout='vertical'
        form={form}
        onFinish={handleSubmit}
        style={{ margin: '0 auto' }}
      >
        {isLoading ? (
          <Spin />
        ) : (
          <TicketFormBuilder disableEntityId={isFromEntityDetailPage} ticketForm={currentForm} />
        )}
        <Flex gap='16px' justify='end' className='mt-4'>
          <button
            type='button'
            onClick={handleCloseModal}
            className='flex items-center gap-1.5 h-9 px-4 rounded-lg border border-solid border-[rgb(232,230,227)] bg-white text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[rgb(245,245,243)] transition-colors uppercase'
          >
            {t('cancel')}
          </button>
          <Form.Item className='m-0'>
            <button
              type='submit'
              className='flex items-center gap-1.5 h-9 px-5 rounded-lg bg-[rgb(237,224,74)] text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[rgb(230,217,60)] transition-colors uppercase'
              disabled={isCreatingTicket}
            >
              {isCreatingTicket && (
                <span className='h-3.5 w-3.5 rounded-full border-2 border-[rgb(57,57,60)] border-t-transparent animate-spin' />
              )}
              {t('submit_create_ticket')}
            </button>
          </Form.Item>
        </Flex>
      </Form>
    </div>
  );
}
